import { useEffect, useState, useRef, useCallback } from "react"
import HomeLayout from "./HomeLayout"
import PulsingLoader from "./ui/PulsingLoader"
import SubmitRequest from "./ui/SubmitRequest"
import useContentLoader from "./hooks/useContentLoader"
import useNavigation from "./hooks/useNavigation"
import useBreadcrumbs from "./hooks/useBreadcrumbs"
import useSearch from "./hooks/useSearch"
// import Value from "aldoo-ra/Value" // Uncomment when reverting to site language
import { getPathFromUrl } from "./urlUtils"
import { expandCategoryPath } from "./categoryUtils"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"

/**
 * Main Help Center component
 */
const HCHome = () => {

  const texts = TextBundle({ id: "help-center" })

  // Force English for help center
  const [currentLanguage] = useState("en")
  
  // To revert to site language, uncomment below and comment out the line above:
  // const [currentLanguage] = Value({
  //   key: "current-site-language",
  //   defaultValue: "en",
  //   persistKey: "current-site-language",
  // })

  // State to force re-render when language changes
  const [refreshKey, setRefreshKey] = useState(0)

  // Load content with language support
  const contentLoader = useContentLoader()

  const {
    selectedContent,
    contentType,
    isLoading,
    error,
    expandedCategories,
    loadArticle,
    loadCategory,
    setExpandedCategories,
    updateUrl,
    refreshContent, // Function from useContentLoader to refresh content
    tocData, // TOC data from the content loader
  } = contentLoader

  // Track previous language to detect changes
  const [prevLanguage, setPrevLanguage] = useState(currentLanguage)

  // Ref to track if we've already reset expanded categories on home page
  const hasResetCategoriesRef = useRef(false)

  // Get current path info from URL
  const currentPath = getPathFromUrl()

  // State to trigger re-renders on navigation
  const [currentUrl, setCurrentUrl] = useState(() =>
    typeof window !== "undefined" ? window.location.pathname : ""
  )

  // Listen for URL changes to trigger re-renders
  useEffect(() => {
    if (typeof window !== "undefined") {
      const updateCurrentUrl = () => {
        const newPath = window.location.pathname
        setCurrentUrl(newPath)
      }

      // Update immediately on mount to sync with current URL
      updateCurrentUrl()

      // Listen for navigation events
      window.addEventListener("popstate", updateCurrentUrl)
      window.addEventListener("helpCenterNavigate", updateCurrentUrl)

      return () => {
        window.removeEventListener("popstate", updateCurrentUrl)
        window.removeEventListener("helpCenterNavigate", updateCurrentUrl)
      }
    }
  }, [])

  // Effect to sync currentUrl with actual window location if they differ
  useEffect(() => {
    if (typeof window !== "undefined") {
      const actualPath = window.location.pathname
      if (currentUrl !== actualPath) {
        setCurrentUrl(actualPath)
      }
    }
  }, [currentUrl])

  // Check if we're on the submit-request page - use both state and direct check for reliability
  const isSubmitRequestPage =
    currentUrl === "/help-center/submit-request" ||
    (typeof window !== "undefined" && window.location.pathname === "/help-center/submit-request")

  // Determine if we're on the home page (no specific content selected)
  const isHomePage = !currentPath?.slug && !selectedContent && !isSubmitRequestPage

  // Effect to handle language changes
  useEffect(() => {
    // Check if language has changed
    if (prevLanguage !== currentLanguage) {
      // Update previous language reference
      setPrevLanguage(currentLanguage)

      // Reset any expanded categories before refresh to prevent null processing
      setExpandedCategories({})

      // Call refreshContent to reload content in the new language
      // This will also reload the TOC data
      refreshContent()

      // Force component re-render with new key
      setRefreshKey((prev) => prev + 1)
    }
  }, [currentLanguage, prevLanguage, refreshContent, setExpandedCategories])

  // Combine multiple home page checks into a single effect to avoid hook order issues
  useEffect(() => {
    // Don't run this logic if we're on the submit-request page
    if (isSubmitRequestPage) {
      return
    }

    // Check if we're on the home page (all currentPath values are null)
    const isOnHomePage =
      currentPath &&
      !currentPath.slug &&
      (!currentPath.type || currentPath.type === null) &&
      (!currentPath.categoryPath || currentPath.categoryPath === null)


    if (isOnHomePage) {
      // Only reset if we haven't done so for this render cycle
      if (!hasResetCategoriesRef.current) {
        // Check if there are expanded categories that need clearing
        if (expandedCategories && Object.keys(expandedCategories).length > 0) {
          setExpandedCategories({})
        }

        // Clean up URL if needed
        if (
          currentUrl !== "/help-center" &&
          currentUrl !== "/help-center/"
        ) {
          window.history.replaceState(null, "", "/help-center")
          setCurrentUrl("/help-center")
        }

        // Mark that we've reset categories
        hasResetCategoriesRef.current = true
      }
    } else {
      // Not on home page, reset the flag
      hasResetCategoriesRef.current = false
    }
  }, [currentPath, expandedCategories, setExpandedCategories, isSubmitRequestPage, currentUrl])

  // Safe wrapper for expandCategoryPath that handles null/invalid values
  const safeExpandCategoryPath = useCallback(
    (path) => {
      if (!path) {
        setExpandedCategories({})
        return
      }

      // Filter out any null/undefined segments before passing to the real function
      const cleanPath =
        typeof path === "string"
          ? path.split("/").filter(Boolean).join("/")
          : Array.isArray(path)
          ? path.filter(Boolean)
          : null

      if (!cleanPath) {
        setExpandedCategories({})
        return
      }

      // Use the original function with clean data
      expandCategoryPath(cleanPath, setExpandedCategories)
    },
    [setExpandedCategories]
  )

  // Initialize navigation with custom handlers to work with the browser
  const handleArticleSelect = useCallback(
    (slug, categoryPath) => {
      // Validate inputs
      if (!slug) return

      // First update URL - this is the source of truth
      updateUrl("article", slug, categoryPath)

      // Expand ONLY the category path for this article
      if (categoryPath) {
        safeExpandCategoryPath(categoryPath)
      } else {
        // If no category path, ensure categories are reset
        setExpandedCategories({})
      }

      // Load the article - this will update the content
      loadArticle(slug, categoryPath)
    },
    [updateUrl, setExpandedCategories, safeExpandCategoryPath, loadArticle]
  )

  const handleCategorySelect = useCallback(
    (slug, parentPath) => {
      if (!slug) return

      // Add validation to prevent invalid arguments
      if (!slug || typeof slug !== "string") {
        return
      }

      // Normalize parentPath - ensure it's a string or empty string (not boolean or null)
      const normalizedParentPath =
        parentPath && typeof parentPath === "string" ? parentPath : ""

      // Use the FULL PATH as the key in expandedCategories
      const fullPath = normalizedParentPath
        ? `${normalizedParentPath}/${slug}`
        : slug

      // Toggle expansion state
      const newExpandedCategories = { ...expandedCategories }
      const wasExpanded = newExpandedCategories[fullPath]
      newExpandedCategories[fullPath] = !wasExpanded

      // Update expanded categories state
      setExpandedCategories(newExpandedCategories)

      // ONLY update URL and load content if this was a content selection action, not just a toggle
      // For toggle-only actions, we don't want to change the current view
      if (!wasExpanded) {
        // Only navigate if we're expanding, not when collapsing
        // Update URL
        updateUrl("category", slug, normalizedParentPath)

        // Load the category content
        loadCategory(fullPath)
      }
    },
    [expandedCategories, setExpandedCategories, updateUrl, loadCategory]
  )

  // Handle search result selection - with improved reset
  const handleSearchResultSelect = useCallback(
    (result) => {
      // Validate result
      if (!result || !result.type) {
        return
      }

      // Reset expanded categories to start with a clean slate
      setExpandedCategories({})

      if (result.type === "article") {
        // Validate article result
        if (!result.item || !result.item.slug) {
          return
        }

        // Use the slugPath array for navigation if available
        let categoryPath = null
        if (result.slugPath && result.slugPath.length > 0) {
          // Join the slugs for URL path, filtering out any "helpcenterroot"
          const filteredPath = result.slugPath.filter(
            (slug) =>
              slug !== "helpcenterroot" && slug !== null && slug !== undefined
          )
          categoryPath = filteredPath.length > 0 ? filteredPath.join("/") : null
        } else if (result.category && result.category.slug) {
          // Fallback to direct category slug
          categoryPath =
            result.category.slug === "helpcenterroot"
              ? null
              : result.category.slug
        }

        // Then proceed with article selection (which will expand only the needed path)
        handleArticleSelect(result.item.slug, categoryPath)
      } else if (result.type === "category") {
        // Validate category result
        if (!result.item || !result.item.slug) {
          return
        }

        const categorySlug =
          result.item.slug === "helpcenterroot" ? "" : result.item.slug
        const parentSlug =
          result.category && result.category.slug
            ? result.category.slug === "helpcenterroot"
              ? ""
              : result.category.slug
            : ""

        // Then proceed with category selection
        handleCategorySelect(categorySlug, parentSlug)
      }
    },
    [setExpandedCategories, handleArticleSelect, handleCategorySelect]
  )

  // Sync expanded categories with URL path on initial load and URL changes
  useEffect(() => {
    if (currentPath.categoryPath && tocData) {
      // Use safe expand function to handle potential invalid paths
      safeExpandCategoryPath(currentPath.categoryPath)
    }
  }, [
    currentPath.categoryPath,
    currentPath.type,
    currentPath.directParentPath,
    tocData,
    safeExpandCategoryPath,
  ])

  // Check if we're on the client side and hydrated
  const [isClient, setIsClient] = useState(false)
  useEffect(() => {
    setIsClient(true)
  }, [])

  // Use the hooks
  const { navigateTo } = useNavigation(contentLoader)

  const { breadcrumbs = [] } = useBreadcrumbs(contentLoader, navigateTo) || {}

  // Initialize search using the API-based hook
  const searchState = useSearch()

  // Show a fullscreen loader ONLY if we're loading TOC and trying to load specific content
  // If we're on home page and no TOC yet, show the layout anyway
  const shouldShowLoader = isLoading && !tocData && isClient && !isHomePage

  if (shouldShowLoader) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <PulsingLoader />
          <Typography className="text-gray_700 mt-4">
            {texts?.loading_hc || "Loading Help Center..."}
          </Typography>
        </div>
      </div>
    )
  }

  // Show error if loading failed
  if (error && !tocData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <Typography variant="error">{error}</Typography>
          <button
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={refreshContent}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // If we're on the submit-request page, render it directly
  if (isSubmitRequestPage) {
    return <SubmitRequest />
  }


  // Render the main layout with all the necessary props
  return (
    <HomeLayout
      isHomePage={isHomePage}
      breadcrumbs={breadcrumbs}
      refreshKey={refreshKey}
      currentLanguage={currentLanguage}
      searchState={searchState}
      onSearchResultSelect={handleSearchResultSelect}
      rootCategoriesData={tocData} // Use TOC data from contentLoader
      onArticleSelect={handleArticleSelect}
      onCategorySelect={handleCategorySelect}
      expandedCategories={expandedCategories}
      currentPath={currentPath}
      isLoading={isLoading}
      error={error}
      selectedContent={selectedContent}
      contentType={contentType}
      navigateTo={navigateTo}
    />
  )
}

export default HCHome
